import { dataSuppressedSetAttribute } from '@hotjar/common';
import { WebResource } from '@hotjar/webapp/features/Playbacks/renderer/web-resource';

export class HjTreeMirror {
  w = window;
  sceneDocument;
  allowlistedDomains = [];
  webResources = [];
  pageVisitUrl;
  siteId;
  recordingId;
  token;
  baseTag;
  childDebugEnabled;
  cssHoverAttribute = 'hotjar-css-hover';
  currentDomain = 'hj-hardcoded-domain.no-match';
  getBlockedIFrameHTML = () => '';

  constructor() {
    this.setAttribute = dataSuppressedSetAttribute(
      this.setAttribute,
      window.document.location.hostname,
    );
  }

  getDomain(url) {
    if (!url) {
      return '';
    }

    var urlParts = url.replace('http://', '').replace('https://', '').split(/[/?#]/);

    var domain = urlParts[0] || '';
    return domain;
  }

  checkIsValidDomain(domains, src) {
    var srcDomain = this.getDomain(src);
    if (!srcDomain) {
      // We allow empty srcDomain.
      // It means the url is same origin.
      // f.e. /index.html
      return true;
    }

    return !!domains.filter((domain) => {
      return srcDomain.indexOf(domain) > -1;
    }).length;
  }

  setChildDebugEnabled = (debug) => {
    this.childDebugEnabled = debug;
  };

  setSceneDocument = (sceneDocument) => {
    this.sceneDocument = sceneDocument;
  };

  setToken = (token) => {
    this.token = token;
  };

  setBaseTag = (baseTag) => {
    this.baseTag = baseTag;
  };

  setRecordingId = (id) => {
    this.recordingId = id;
  };

  setSiteId = (id) => {
    this.siteId = id;
  };

  setWebResources = (resources) => {
    this.webResources = resources;
  };

  setAllowlistedDomains = (domains) => {
    this.allowlistedDomains = domains;
  };

  setPageVisitUrl = (url) => {
    this.pageVisitUrl = url;
  };
  setCurrentDomain = (url) => {
    if (url) {
      this.currentDomain = this.getDomain(url);
    }
  };
  setBlockedIFrameHTMLGetter = (fn) => {
    this.getBlockedIFrameHTML = fn;
  };

  // Check if a string exists and return it in lowercase, otherwise a blank string.
  checkEmptyAndReturnLower = (str) => {
    return typeof str === 'string' ? str.toLowerCase() : '';
  };

  ignoredElement = (e) => {
    var ignored = this.sceneDocument.createElement('IGNORE-' + e.tagName);
    ignored.style.display = 'none';
    return ignored;
  };

  createElement = (e) => {
    if (this.childDebugEnabled) {
      /* eslint-disable-next-line no-console */
      console.debug(e);
    }

    var node;

    if (typeof e === 'object' && typeof e.tagName === 'string') {
      // Clean up tagName by only allow alphanumeric characters.
      // Clean up tagName by only allow alphanumeric characters.
      e.tagName = e.tagName.replace(/[^a-zA-Z0-9-_]/g, '');

      if (e.tagName === 'SCRIPT' || e.tagName === 'NOSCRIPT') {
        // 1. No JS is allowed, so hide <script> elements.
        // 2. As JS is not allowed, browser would render <noscript> elements, so we hide them.
        return this.ignoredElement(e);
      } else if (e.tagName === 'LINK') {
        // Remove integrity checks as it may be a CSS we have stored that was rewritten and would now
        // fail the verification.
        if (e.attributes['integrity']) {
          delete e.attributes['integrity'];
        }

        // To retrieve CSS we have stored, we need cookies to be sent.
        if (e.attributes['crossorigin']) {
          delete e.attributes['crossorigin'];
        }

        if (e.attributes['href'] && e.attributes.href.indexOf('blob:') !== -1) {
          delete e.attributes['href'];
        }

        // Ignore script preloads.
        if (
          this.checkEmptyAndReturnLower(e.attributes['as']) === 'script' &&
          this.checkEmptyAndReturnLower(e.attributes['rel']) === 'preload'
        ) {
          return this.ignoredElement(e);
        }

        // Ignore CSS from browser extensions.
        if (this.checkEmptyAndReturnLower(e.attributes['href']).indexOf('-extension://') !== -1) {
          return this.ignoredElement(e);
        }
      } else if (e.tagName === 'HEAD') {
        // When creating the head element, add the base tag.
        node = this.sceneDocument.createElement('HEAD');
        node.appendChild(this.sceneDocument.createElement('BASE'));

        // If the page already has a base tag, add it anyway to the very top.
        if (this.baseTag) {
          node.firstChild.href = this.fixBaseTagHref(
            this.baseTag.attributes.href,
            this.pageVisitUrl,
          );
        } else {
          node.firstChild.href = this.pageVisitUrl;
        }
        return node;
      } else if (e.tagName === 'STYLE') {
        for (var i = 0; i < (e.childNodes || []).length; i++) {
          var childNode = e.childNodes[i];
          if (childNode.textContent) {
            childNode.textContent = childNode.textContent.replace(
              /:hover/g,
              '[' + this.cssHoverAttribute + ']',
            );
          }
        }
      } else if (
        /* Remove all instances of:
                        <meta content="DENY" http-equiv="X-FRAME-OPTIONS" />
                        <meta http-equiv="Content-Security-Policy" content="..." />
                    */
        e.tagName === 'META' &&
        ((typeof e.attributes === 'object' &&
          this.checkEmptyAndReturnLower(e.attributes['http-equiv']) === 'x-frame-options' &&
          this.checkEmptyAndReturnLower(e.attributes['content']) === 'deny') ||
          this.checkEmptyAndReturnLower(e.attributes['http-equiv']) === 'content-security-policy')
      ) {
        return this.ignoredElement(e);
      } else if (e.tagName === 'INPUT') {
        node = this.sceneDocument.createElement(e.tagName);
        node.setAttribute('autocomplete', 'nope');
        node.setAttribute('readonly', '');
        return node;
      } else if (e.tagName === 'IFRAME') {
        // Remove our iframe from the tracking script as it would fail to execute because of sandboxing.
        if (e.attributes['id'] === '_hjRemoteVarsFrame') {
          return this.ignoredElement(e);
        }

        // Browser extensions: chrome-extension:// moz-extension:// ms-browser-extension://
        if (this.checkEmptyAndReturnLower(e.attributes['src']).indexOf('-extension://') !== -1) {
          return this.ignoredElement(e);
        }

        // Replace the iframe's SRC with a page we host ourselves.
        // NOTE: The data-hj-allow-iframe is used internally for testing,
        //       so we can check that XSS attacks cannot occur through our recordings.
        if (
          typeof e.attributes['data-hj-allow-iframe'] === 'undefined' ||
          !this.checkIsValidDomain(
            this.allowlistedDomains.concat(this.currentDomain),
            e.attributes['src'],
          )
        ) {
          if (typeof e.attributes['src'] !== 'undefined') {
            e.attributes['data-hj-original-src'] = e.attributes['src'];
          }

          e.attributes['src'] = 'about:blank';
          e.attributes['srcdoc'] = this.getBlockedIFrameHTML();
          e.attributes['data-iframe-blocked'] = '';
        }
      }

      if (e.tagName === 'LINK') {
        var element = this.sceneDocument.createElement(e.tagName);

        if (
          !e.attributes.href ||
          (e.attributes['href'] && e.attributes.href.indexOf('blob:') !== -1)
        ) {
          delete e.attributes['href'];
          // If we don't set an href, playing back inserted rules (from blobs) on a link element will fail
          element.setAttribute('href', '#');
        }

        return element;
      }

      if (e && e.namespaceURI) {
        // This is where SVG parent elements get created. For more info, see ACT-499.
        return this.sceneDocument.createElementNS(e.namespaceURI, e.tagName.toLowerCase());
      }

      // Otherwise, just create the element.
      return this.sceneDocument.createElement(e.tagName);
    } else {
      return false;
    }
  };

  setAttribute = (node, attribute, value) => {
    // Only allow alphanumeric, -, _ and : characters in attributes.
    attribute = attribute.replace(/[^a-z0-9-_:]/gi, '');

    // If the page already had a base tag specified, we need to fix it in case they put in a relative path.
    if (node.tagName === 'BASE' && attribute === 'href') {
      value = this.fixBaseTagHref(value, this.pageVisitUrl);
    } else if (node.tagName === 'LINK' && attribute === 'href') {
      // Check if we have stored CSS for this URL.
      value = WebResource.findStoredCss(value, {
        webResources: this.webResources,
        siteId: this.siteId,
        recordingId: this.recordingId,
        sceneDocument: this.sceneDocument,
        token: this.token,
      });
    } else if (node.tagName === 'INPUT' && attribute === 'autofocus') {
      // Autofocus is not allowed in a sandboxed iframe (without allow-scripts).
      node.setAttribute('ignore-' + attribute, value);
      return true;
    }

    if (node.tagName === 'LINK' && attribute === 'href' && value.indexOf('blob:') !== -1) {
      return true;
    }

    // Try to remove the most common usage of JavaScript in HTML to decrease the amount of security errors
    // in browser's console caused by the fact that we are sandboxed in the preview-scene iframe.
    if (
      (attribute === 'src' || attribute === 'href') &&
      value.toLowerCase().indexOf('javascript:') !== -1
    ) {
      return true;
    }

    if (
      isNaN(attribute.charAt(0)) &&
      attribute.charAt(0) !== '-' &&
      attribute.indexOf('on') !== 0
    ) {
      if (attribute.indexOf('xlink:') !== -1 && node.setAttributeNS) {
        node.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', value);
      } else if (node.setAttribute) {
        node.setAttribute(attribute, value);
      }
      if (!node.setAttribute) {
        // debugger;
      }
    }

    return true;
  };

  fixBaseTagHref = (path, pageVisitUrl) => {
    const pageURL = document.createElement('a');
    pageURL.setAttribute('href', pageVisitUrl);

    var isPathFullUrl = path.substr(0, 4) === 'http' || path.substr(0, 2) === '//';
    if (isPathFullUrl) {
      return path;
    }

    var isPathAbsolute = path.substr(0, 1) === '/';

    var newPath;
    if (isPathAbsolute) {
      newPath = path;
    } else {
      var endsInSlash = pageURL.pathname.slice(-1) === '/';

      // remove http://www.hotjar.com/(index.html) and http://www.hotjar.com/(index)
      // and only keep http://www.hotjar.com/index/
      var pathname = endsInSlash
        ? pageURL.pathname
        : pageURL.pathname.substring(0, pageURL.pathname.lastIndexOf('/') + 1);

      newPath = pathname + path;
    }

    var urlWithoutPath =
      pageURL.protocol + '//' + pageURL.hostname + (pageURL.port ? ':' + pageURL.port : '');

    return new URL(urlWithoutPath + newPath).href;
  };
}
